import { Icon } from "@legal-500/legal500-design-system";
import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, useAuthActions } from "../../contexts/AuthContext";
import { ROUTES } from "../../routes";
import styles from "./UserMenu.module.css";
import { publicRuntimeConfig } from "../../config/publicRuntimeConfig";

type UserMenuProps = {
  theme: "light" | "dark";
  toggleTheme: () => void;
  /** Avatar only, for the collapsed sidebar rail. */
  compact?: boolean;
};

const isAuthDisabled = publicRuntimeConfig.authDisabled;

export function UserMenu({ theme, toggleTheme, compact = false }: UserMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { user, tenantsState } = useAuth();
  const { logout } = useAuthActions();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleLogout = () => {
    logout();
    setIsOpen(false);
  };

  const handleSettings = () => {
    navigate(ROUTES.account.me);
    setIsOpen(false);
  };

  const handleThemeToggle = () => {
    toggleTheme();
    setIsOpen(false);
  };

  const displayName = user?.name || user?.email || "Unknown user";
  const displaySubtitle = isAuthDisabled ? "Dev Mode" : tenantsState?.activeTenant?.name;

  return (
    <div className={styles.userMenuContainer} ref={menuRef}>
      <button
        className={`${styles.userMenuButton} ${compact ? styles.userMenuButtonCompact : ""}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="true"
        // Collapsed, the name is not rendered, so it has to come from the label.
        aria-label={compact ? displayName : undefined}
        title={compact ? displayName : undefined}
      >
        <div className={styles.userInfo}>
          <div className={styles.avatarContainer}>
            {user?.profilePictureUrl ? (
              <img
                src={user.profilePictureUrl}
                alt={user.name || "User avatar"}
                className={styles.avatarImage}
              />
            ) : (
              <div className={styles.avatarPlaceholder}>
                <Icon icon="user" size={{ width: 1, height: 1 }} />
              </div>
            )}
          </div>
          {!compact && (
            <div className={styles.userDetails}>
              <span className={styles.userName}>{displayName}</span>
              {displaySubtitle && <span className={styles.firmName}>{displaySubtitle}</span>}
            </div>
          )}
        </div>
        {!compact && (
          <Icon
            icon="chevron-down"
            size={{ width: 1, height: 1 }}
            className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ""}`}
          />
        )}
      </button>

      {isOpen && (
        <div className={`${styles.menuDropdown} ${compact ? styles.menuDropdownCompact : ""}`}>
          <button className={styles.menuItem} onClick={handleThemeToggle}>
            <Icon icon="circle-half" size={{ width: 1.25, height: 1.25 }} />
            <span>{theme === "light" ? "Dark Mode" : "Light Mode"}</span>
          </button>
          <button className={styles.menuItem} onClick={handleSettings}>
            <Icon icon="settings" size={{ width: 1.25, height: 1.25 }} />
            <span>Settings</span>
          </button>
          <div className={styles.menuDivider} />
          <button className={styles.menuItem} onClick={handleLogout}>
            <Icon icon="logout" size={{ width: 1.25, height: 1.25 }} />
            <span>Logout</span>
          </button>
        </div>
      )}
    </div>
  );
}
